// Bosses, artifacts, curse and the final-boss dying sequence. Ported from boss.c.

import { rand, RandomDir } from './rng';
import { G, rooms, boss_names, percent_required } from './state';
import { RoomConnection } from './types';
import { IsSolid } from './tiles';
import { Get, Put, GetRoom, Paint } from './mapgen';
import { getImg, getAssetScale } from '../render/assets';
import {
  screen,
  blitGame,
  DrawRect,
  DrawCircle,
  DrawCircleEx,
  Arc,
  draw_text,
} from '../render/surface';
import { BASE_TILE, PLAYERW, PLAYERH, TILE, S, SCREEN_W, SCREEN_H } from './constants';
import { SND_Pos } from '../audio/index';
import {
  SpawnBullet,
  SpawnLaser,
  PlayerDir,
  CullEnemies,
  SoupUpEnemies,
  CurseEnemies,
} from './demon';

const M_PI = Math.PI;
const tr = Math.trunc;

function sqr(x: number): number {
  return x * x;
}
function dist(x1: number, y1: number, x2: number, y2: number): number {
  return tr(Math.sqrt(sqr(x2 - x1) + sqr(y2 - y1)));
}

// --- boss state (module-local statics) -----------------------------------
let boss_x = 0;
let boss_y = 0;
let boss_hp = 0;
let boss_tail_len = 0;
let boss_breakpoint = 0;
let boss_flash = 0;
let boss_new_life = 0;
let boss_ox = 0;
let boss_oy = 0;
let final_boss_dlg = 0;
let boss_dmgrate = 1;
let boss_dir = 0;

let boss_m_heads = 0;
const boss_m_hx = [0, 0, 0, 0];
const boss_m_hy = [0, 0, 0, 0];
const boss_m_hd = [0, 0, 0, 0];

let boss_2h_dir = 0;
let boss_2h_dst = S(64);

interface DlgBox {
  pos: number;
  lines: number;
  last: number;
  txt: string;
}

const dtext: DlgBox[][] = [
  [
    { pos: 0, lines: 5, last: 0, txt:
      'MERIDIAN:\n  \n  Different...\n  \n  Your PSI is different. Your magic is perverse. Poisonous. Treacherous.' },
    { pos: 1, lines: 5, last: 0, txt:
      'MERIT:\n  \n  Nothing but darkness carried on the wind of a curse. It\'s amazing that you\n  are able to communicate with me at all. Surely a statement on the perversity\n  of my magic, coming from you, is more than a little bit hypocritical.' },
    { pos: 0, lines: 6, last: 1, txt:
      'MERIDIAN:\n  \n  So. You\'ve even brought that tainted thing with you.\n  That does not really surprise me, but nonetheless, your weak, impure PSI\n  will do you little good against one manifest of shadows. Pitiful thing from\n  the surface, MERIDIAN shall return you to the dust from whence you came!' },
  ],
  [
    { pos: 0, lines: 5, last: 0, txt:
      'ATARAXIA:\n  \n  It felt nearly as inconsequential as a stilling in the breeze, but there was\n  more to it, wasn\'t it? It feels as though I lost a part of myself. Was that\n  your doing?' },
    { pos: 1, lines: 6, last: 0, txt:
      'MERIT:\n  \n  Moving Ochra\'s Keys from the ley lines was only the start, am I correct?\n  Nonetheless, what you\'ve already caused is enough. The Keys were placed to\n  prevent corruption in the PSI. Moving them renders the PSI unusable. There\n  is nothing to be gained from THAT.' },
    { pos: 0, lines: 8, last: 0, txt:
      'ATARAXIA:\n  \n  Statements like that belie your youthful naivety and ignorance. There is no\n  way to make magic unusable. What you see now is the other side of PSI that\n  pitiful beings such as yourself turn a blind eye to. Like the sun at dawn\n  the sun at dusk, it is nonetheless the same sun, and each beautiful in its\n  own way. PSI isn\'t just for things like you, but for the entire cosmos.\n  Nothing can be gained by preventing it from taking its natural course.' },
    { pos: 1, lines: 8, last: 0, txt:
      'MERIT:\n  \n  You delude yourself with the crazed whisperings from the muck that spawned\n  you. You say that this PSI is still PSI, but it is not. PSI is the energy of\n  creation. This is the energy of destruction. PSI is a pure force, gathered\n  from around me and targeted with my mind. This is a foul mixture, existing\n  as half-mad corruptions like you. It can only break apart when confronted\n  with true power.' },
    { pos: 0, lines: 6, last: 1, txt:
      'ATARAXIA:\n  \n  You realise that not even your fellow PSI users agree with you? But, it is\n  of little consequence. I\'ll show you the true beauty of that which you shun\n  by smothering you in it and turning your flesh into fuel for the PSI that\n  runs through the veins of this Dome!' },
  ],
  [
    { pos: 0, lines: 9, last: 0, txt:
      'MERODACH:\n  \n  e v e r y t h i n g   h a s   t u r n e d   t o   d a r k n e s s\n  \n  n o   s o u n d   n o   l i g h t   n o   s e n s a t i o n\n  \n  w a s   t h i s   w h a t   y o u   w e r e   a f t e r   f r o m\n  \n  t h e   b e g i n n i n g ?' },
    { pos: 0, lines: 7, last: 1, txt:
      'MERODACH\n  \n  i \' l l   w r a p   m y   r e m a i n i n g   P S I   a r o u n d   y o u\n  \n  a n d   e x t i n g u i s h   t h a t   l i f e   o f   y o u r s\n  \n  l e t \' s   s e e   h o w   y o u   e n j o y   e t e r n a l   r e s t' },
  ],
  [
    { pos: 1, lines: 6, last: 0, txt:
      'MERIT:\n  \n  ANIXIL? So, that\'s how it is. I assume you worked out a way of using the\n  PSI in this state. So, not happy with your allotted PSI from the Dome, you\n  decided to render it in this form and take it all for yourself.\n  What was your reasoning for doing something this rash, though?' },
    { pos: 0, lines: 9, last: 0, txt:
      'WERVYN ANIXIL:\n  \n  All you seem to do is complain. That\'s what I\'ve discovered through this,\n  and it\'s definitely part of the reason I did it. I do creative things like\n  this sort of \'punishment\' because it lets me better understand the nature of\n  all the forces involved. It tells me that you, MERIT, really do not have a\n  clue how to relate to other PSI users, and that for the most part you\'re\n  entirely self-absorbed. It tells me that, while PSI users in general doesn\'t\n  really \'get\' the ethos of the forces involved here, and that the rerendered' },
    { pos: 0, lines: 9, last: 0, txt:
      'WERVYN ANIXIL:\n  \n  PSI, as intelligent as it is, pretty much entirely understands it, though it\n  was mistaken that the rendering of this form was intentional.\n  \n  Regarding that, by the way, THAT is the reason I thought you needed a\n  punishment. Making ridiculously inefficient, wasteful use of the limited PSI\n  we have available wouldn\'t have been a problem if it was just the normal PSI\n  like we\'d planned it to be. But you knew full well that PSI users who wanted' },
    { pos: 0, lines: 9, last: 0, txt:
      'WERVYN ANIXIL:\n  \n  nothing to do with it were being greatly inconvenienced by usage clogging up\n  the limited ley-lines we have. That makes you an asshole, a deliberate prick\n  to everyone in the world because you thought you could get away with it, and\n  that it wouldn\'t matter.\n  \n  I also take issue with this nonsense about your punishment being severe,\n  because it\'s not. You can still use your PSI as you used it before, to a' },
    { pos: 0, lines: 9, last: 0, txt:
      'WERVYN ANIXIL:\n  \n  similar level of effectiveness. I specifically allowed access to THIS form\n  of PSI so that you couldn\'t complain that you couldn\'t use magic. Now\n  exiling you to the Dome had multiple meanings. One was to effectively tell\n  you, \'I don\'t think you have anything of value to do, so I\'m putting you in\n  a place where no one does anything of value.\' I still think that, by the\n  way. Another was, like I said, to see what you would do. Maybe you would\n  actually cast something worth the PSI, then. Or maybe you would take the' },
    { pos: 0, lines: 8, last: 0, txt:
      'WERVYN ANIXIL:\n  \n  hint and just stop it for the week until I decided you\'d had enough. But you\n  couldn\'t do either of those things, because all you do is complain.\n  \n  I want you to take a look at yourself and ask what you want to accomplish\n  here, and what you think is keeping you here. Obviously you have some\n  attachment, even though you keep saying the new PSI is worthless. This isn\'t' },
    { pos: 0, lines: 9, last: 0, txt:
      'WERVYN ANIXIL:\n  \n  true, of course, ATARAXIA really captures a good point here. For the most\n  part, PSI users just find you extremely annoying because you continually set\n  yourself at odds with pretty much every kind of PSI usage but your own, and\n  then complain about it. Do you want things to be different? They certainly\n  can be. \'EVEN IF I DO CHANGE, THEN MY PSI WON\'T BE GOOD ENOUGH!\' That\'s\n  bullshit. All other PSI users seem to be on the path to figuring out how to\n  just use it. You can too.' },
    { pos: 0, lines: 9, last: 0, txt:
      'WERVYN ANIXIL:\n  \n  Your \'punishment\' is up tomorrow, I\'d only planned to do it for a week. Of\n  course, you can continue complaining, and I may have to think of something\n  else to do to you. But I\'m not going to kill you. If you want to leave this\n  mortal coil because you can\'t take it anymore, then leave. If you don\'t want\n  to leave, then start working out your issues with the new PSI, I actually\n  would love to help. And if you don\'t want to do that, then I\'m sorry, but\n  you\'re going to continue being useless at magic, because the way you are' },
    { pos: 0, lines: 4, last: 0, txt:
      'WERVYN ANIXIL:\n  \n  now, it\'s not surprising at all that your PSI ability is as stunted as it\n  is.' },
    { pos: 1, lines: 4, last: 0, txt:
      'MERIT:\n  \n  The tainted PSI has driven even you mad. Mad with power. This is a blow I\n  I must strike, for everyone. For humanity.' },
    { pos: 0, lines: 3, last: 1, txt:
      'WERVYN ANIXIL:\n  \n  I\'ll meet you at the point of diminishing returns.' },
  ],
  [
    { pos: 1, lines: 8, last: 0, txt:
      'MERIT:\n  \n  ANIXIL? So, that\'s how it is. I assume you worked out a way of using the\n  PSI in this state. So, not happy with your allotted PSI from the Dome, you\n  decided to render it in this form and take it all for yourself.\n  What was your reasoning for doing something this rash, though?\n  \n  That was what you wanted to hear, right?' },
    { pos: 0, lines: 7, last: 0, txt:
      'WERVYN ANIXIL????:\n  \n  All you seem to do is complain. That\'s what I\'ve discovered through this,\n  and it\'s definitely part of the ... ... ... ... ... ... ... ... ... ... ...\n  ... ... ... ... ... ... ... ... ... ... ... ... ... ... ... ... ... ... ...\n  ... ... ... ... ... ... ... ... ... ... ... ... ... ... ... ... ... ... ...\n  ... ... oh.' },
    { pos: 1, lines: 6, last: 0, txt:
      'MERIT:\n  \n  If I\'ve learned anything in life, it\'s that things are often not quite as\n  they seem. You couldn\'t use the Agate Knife, so you attempted to hide it.\n  \n  Unfortunately for you, I found it and recognised it.' },
    { pos: 0, lines: 7, last: 0, txt:
      'WERVYN ANIXIL????:\n  \n  . . . . no. It\'s not just recognising the Knife at all. You were actually\n  able to use it, too.\n  \n  So. You too knew the secret?' },
    { pos: 1, lines: 5, last: 0, txt:
      'MERIT:\n  \n  I was one of the few that he told.\n  \n  And so I know.' },
    { pos: 0, lines: 3, last: 0, txt:
      '????:\n  \n  Umm...' },
    { pos: 1, lines: 7, last: 0, txt:
      'MERIT:\n  \n  Well, it wasn\'t a bad attempt. If I hadn\'t found THAT, I would never have\n  known.\n  \n  Now that that\'s taken care of, I might as well finish what I came here for.\n  Prepare yourself.' },
    { pos: 0, lines: 3, last: 1, txt:
      '????:\n  \n  I won\'t hold back! There\'s not a lot of point, now.' },
  ],
];

function artifactSpr() {
  return getImg('dat/i/artifacts.png');
}

export function BossDialog(): void {
  let dialog_set = G.current_boss;
  if (G.current_boss === 3 && G.player_shield === 30) dialog_set = 4;

  const lines = dtext[dialog_set][G.boss_dlg - 1].lines;
  if (G.enter_pressed) {
    if (dtext[dialog_set][G.boss_dlg - 1].last) {
      G.boss_dlg = 0;
      G.boss_fight_mode = 2;
    } else {
      G.boss_dlg++;
    }
    G.enter_pressed = 0;
  }
  if (G.boss_dlg > 0) {
    let ypos: number;
    if (dtext[dialog_set][G.boss_dlg - 1].pos === 0) ypos = S(29);
    else ypos = S(466) - lines * S(10) - S(14);
    DrawRect(0, ypos, SCREEN_W, lines * S(10) + S(14), 0);
    DrawRect(S(2), ypos + S(2), S(636), lines * S(10) + S(10), 40);
    DrawRect(S(4), ypos + S(4), S(632), lines * S(10) + S(6), 80);
    draw_text(S(8), ypos + S(8), dtext[dialog_set][G.boss_dlg - 1].txt, 255);
  }
}

export function InitBossVars(): void {
  G.current_boss_room = 0;
  G.current_boss = 0;
  boss_tail_len = 0;
  G.boss_fight_mode = 0;
  boss_flash = 0;
  boss_new_life = 0;
  final_boss_dlg = 0;
}

function Curse(): void {
  G.checkpoint_x = rooms[G.place_of_power].w * S(16) + rooms[G.place_of_power].x * TILE;
  G.checkpoint_y = rooms[G.place_of_power].h * S(16) + rooms[G.place_of_power].y * TILE;

  rooms[0].room_type = 2;
  Paint(rooms[0].x + 1, rooms[0].y + 1, rooms[0].w - 2, rooms[0].h - 2, 'dat/d/fbossroom.loc');

  for (let i = 0; i < 3000; i++) {
    if (!rooms[i].visited) {
      let rc: RoomConnection | null = rooms[i].con;
      while (rc !== null) {
        const x = rc.x2;
        const y = rc.y2;
        let tile = Get(x, y);
        if (tile >= 38 && tile <= 41) tile = tile - 38 + 4;
        if (tile >= 21 && tile <= 24) tile = tile - 21 + 4;
        if (tile >= 13 && tile <= 16) tile = tile - 13 + 4;
        Put(x, y, tile, GetRoom(x, y));
        rc = rc.n;
      }
      rooms[i].con = null;
      rooms[i].connections = 0;
    }
  }

  CurseEnemies();
}

let dpo_tick = 0;
let dpo_collect = 0;

export function DrawPowerObject(): void {
  let p_obj = rooms[G.player_room].room_param;
  if (G.place_of_power === G.player_room) p_obj = 3;

  const n_artifacts = G.current_boss + G.artifacts[8] + G.artifacts[9] + G.artifacts[10];
  let required_enemies = tr((G.total_enemies * percent_required[n_artifacts]) / 100);
  if (rooms[G.player_room].room_type === 4) required_enemies = 0;

  let hover_v = S(16);
  let off_v = S(48);

  const p_x = tr((rooms[G.player_room].w * TILE) / 2 - S(16)) + rooms[G.player_room].x * TILE;

  if (!G.game_paused) {
    if (rooms[G.player_room].room_type === 5 || rooms[G.player_room].room_type === 6) {
      if (CanGetArtifact()) {
        if (
          Get(tr((G.player_x + PLAYERW / 2) / TILE), tr((G.player_y + PLAYERH / 2) / TILE)) === 42 ||
          PlayerDistLocal(
            rooms[G.player_room].w * S(16) + rooms[G.player_room].x * TILE,
            rooms[G.player_room].h * S(16) + rooms[G.player_room].y * TILE
          ) < TILE
        ) {
          if (rooms[G.player_room].enemies === 0) {
            off_v = S(48) - tr((dpo_collect * S(48)) / 100);
            hover_v = S(16) - tr((dpo_collect * S(16)) / 100);

            DrawCircle(
              G.player_x + PLAYERW / 2 - G.scroll_x,
              G.player_y + PLAYERH / 2 - G.scroll_y,
              (100 - dpo_collect) * 4 + 1,
              (rand() % 192) + 64
            );

            dpo_collect++;
            if (dpo_collect === 1) SND_Pos('dat/a/crystal2.wav', 100, 0);
            if (dpo_collect > 100) {
              dpo_collect = 0;
              rooms[G.player_room].room_type = 4;
              G.artifacts[8 + p_obj] = 1;
              G.specialmessage = 30 + p_obj;
              G.specialmessagetimer = 120;
              if (p_obj === 3) Curse();
            }
          }
        }
      }
    }
  }

  const p_y =
    tr((rooms[G.player_room].h * TILE) / 2 - S(16)) +
    rooms[G.player_room].y * TILE -
    off_v +
    Math.sin(dpo_tick / 20.0) * hover_v;

  if (G.killed_enemies >= required_enemies) {
    DrawCircle(
      p_x + S(16) - G.scroll_x,
      p_y + S(16) - G.scroll_y,
      S(34) + (rand() % 5) + dpo_collect * S(4) + 1,
      (rand() % 128) + 128
    );
  }
  blitGame(
    artifactSpr(),
    { x: (8 + p_obj) * BASE_TILE, y: 0, w: BASE_TILE, h: BASE_TILE },
    screen,
    p_x - G.scroll_x,
    p_y - G.scroll_y,
    getAssetScale('dat/i/artifacts.png')
  );

  const n = tr(Math.sqrt(required_enemies - G.killed_enemies)) + 5;
  for (let i = 0; i < n; i++) {
    const ddir = ((rand() % 256) * M_PI * 2) / 256;
    const dmag = rand() % S(20);
    const dx = p_x + S(16) + Math.cos(ddir) * dmag;
    const dy = p_y + S(16) + Math.sin(ddir) * dmag;
    DrawCircle(dx - G.scroll_x, dy - G.scroll_y, (rand() % 5) + 1, 0);
    DrawCircle(dx - G.scroll_x, dy - G.scroll_y, (rand() % 3) + 1, 64);
  }
  dpo_tick++;
}

function PlayerDistLocal(x: number, y: number): number {
  return tr(Math.sqrt(sqr(x - (G.player_x + S(8))) + sqr(y - (G.player_y + S(12)))));
}

export function DrawBossHP(bar_length: number): void {
  blitGame(getImg('dat/i/boss_icon.png'), null, screen, 0, S(29), getAssetScale('dat/i/boss_icon.png'));
  DrawRect(S(16), S(28), S(624), S(17), 0);
  DrawRect(S(17), S(29), S(622), S(15), 32);
  DrawRect(S(18), S(30), S(620), S(13), 64);

  const draw_col = 128 + tr((boss_hp * 127) / 1000);
  let draw_amt: number;
  if (bar_length < 100) draw_amt = tr((bar_length * S(614)) / 100);
  else draw_amt = tr((boss_hp * S(614)) / 1000);

  DrawRect(S(20), S(32), draw_amt + S(2), S(9), tr((draw_col * 2) / 3));
  DrawRect(S(21), S(33), draw_amt, S(7), draw_col);

  if (G.current_boss === 3 && G.player_shield === 30) {
    draw_text(S(22), S(33), '??????????????', 0);
  } else {
    draw_text(S(22), S(33), boss_names[G.current_boss], 0);
  }
}

function PDir(x1: number, y1: number, x2: number, y2: number): number {
  return Math.atan2(y2 - y1, x2 - x1);
}

function CHDir(original_dir: number, new_dir: number, dir_delta: number): number {
  while (original_dir < 0) original_dir += M_PI * 2;
  while (new_dir < 0) new_dir += M_PI * 2;

  original_dir = (original_dir % (M_PI * 2)) + M_PI * 2;
  new_dir = (new_dir % (M_PI * 2)) + M_PI * 2;

  if (Math.abs(original_dir - new_dir) <= dir_delta) return new_dir;
  if (Math.abs(original_dir + M_PI * 2 - new_dir) <= dir_delta) return new_dir;
  if (Math.abs(original_dir - (new_dir + M_PI * 2)) <= dir_delta) return new_dir;

  if (original_dir > new_dir) {
    if (original_dir - new_dir <= M_PI) return original_dir - dir_delta;
    if (new_dir + M_PI * 2 - original_dir <= M_PI) return original_dir + dir_delta;
  } else {
    if (new_dir - original_dir <= M_PI) return original_dir + dir_delta;
    if (original_dir + M_PI * 2 - new_dir <= M_PI) return original_dir - dir_delta;
  }
  return original_dir;
}

export function TryHurtBoss(x: number, y: number, range: number, power: number): void {
  if (boss_new_life) return;
  if (dist(x, y, boss_x, boss_y) <= range) {
    const atk_power = tr((400 * power) / G.circuit_size) + tr(power / 75);
    if (atk_power > boss_breakpoint) {
      boss_hp -= tr((atk_power - boss_breakpoint) * boss_dmgrate);
      SND_Pos('dat/a/enemyhit.wav', 128, tr(dist(x, y, boss_x, boss_y) / 4));
      boss_flash = 40;
      if (boss_hp <= 0) {
        boss_new_life = 1;
        boss_hp = 0;
      }
    }
  }
}

function BossMovement(move_x: number, move_y: number): number {
  const c3 = G.current_boss === 3 ? 1 : 0;
  if (!IsSolid(Get(tr((move_x - S(12) + c3 * S(6)) / TILE), tr((move_y - S(12) + c3 * S(4)) / TILE)))) {
    if (!IsSolid(Get(tr((move_x + S(12) - c3 * S(6)) / TILE), tr((move_y - S(12) + c3 * S(4)) / TILE)))) {
      if (!IsSolid(Get(tr((move_x - S(12) + c3 * S(6)) / TILE), tr((move_y + S(12) - c3 * S(4)) / TILE)))) {
        if (!IsSolid(Get(tr((move_x + S(12) - c3 * S(6)) / TILE), tr((move_y + S(12) - c3 * S(4)) / TILE)))) {
          return 1;
        }
      }
    }
  }
  return 0;
}

// DrawBoss statics
let db_t = 0;
const db_tail_x = new Array(11).fill(0);
const db_tail_y = new Array(11).fill(0);

function DrawBoss(): void {
  db_t++;
  const flash_coeff = boss_flash > 0 && tr(boss_flash / 3) % 2 ? 255 : 0;
  if (boss_flash > 0) boss_flash--;

  switch (G.current_boss) {
    case 0: {
      const spr = getImg('dat/i/boss1.png');
      if (boss_tail_len === 0) {
        db_tail_x[boss_tail_len] = boss_x;
        db_tail_y[boss_tail_len] = boss_y;
        boss_tail_len++;
      } else {
        if (dist(db_tail_x[0], db_tail_y[0], boss_x, boss_y) >= S(24)) {
          if (boss_tail_len < 10) boss_tail_len++;
          for (let i = 9; i >= 0; i--) {
            db_tail_x[i + 1] = db_tail_x[i];
            db_tail_y[i + 1] = db_tail_y[i];
          }
          db_tail_x[0] = boss_x;
          db_tail_y[0] = boss_y;
        } else {
          for (let i = 0; i < boss_tail_len; i++) {
            db_tail_x[i] += -1 + (rand() % 3);
            db_tail_y[i] += -1 + (rand() % 3);
          }
        }
      }
      for (let i = 0; i < boss_tail_len; i++)
        DrawCircleEx(db_tail_x[i] - G.scroll_x, db_tail_y[i] - G.scroll_y, S(48) - i * S(3), 0, 96 ^ flash_coeff);
      for (let i = 0; i < boss_tail_len; i++)
        DrawCircleEx(db_tail_x[i] - G.scroll_x, db_tail_y[i] - G.scroll_y, S(44) - i * S(3), 0, 0 ^ flash_coeff);
      blitGame(spr, null, screen, boss_x - S(16) - G.scroll_x, boss_y - S(16) - G.scroll_y, getAssetScale('dat/i/boss1.png'));
      break;
    }
    case 1: {
      const spr = getImg('dat/i/boss2.png');
      const heads = 5 - G.boss_lives;
      const h_dist = (M_PI * 2) / heads;

      let new_dst = S(64) + Math.sin(db_t / 80.0) * S(24);
      let new_dir = boss_2h_dir + (rand() % 16) / 128.0 - (rand() % 16) / 128.0;

      let check_pass = 1;
      for (let i = 0; i < heads; i++) {
        const mx = boss_x + Math.cos(new_dir + h_dist * i) * new_dst;
        const my = boss_y + Math.sin(new_dir + h_dist * i) * new_dst;
        if (!BossMovement(mx, my)) check_pass = 0;
      }
      if (check_pass) {
        boss_2h_dst = new_dst;
        boss_2h_dir = new_dir;
      }

      const hx: number[] = [];
      const hy: number[] = [];
      for (let i = 0; i < heads; i++) {
        hx[i] = boss_x + Math.cos(boss_2h_dir + h_dist * i) * boss_2h_dst;
        hy[i] = boss_y + Math.sin(boss_2h_dir + h_dist * i) * boss_2h_dst;
      }
      for (let j = 0; j < heads; j++) {
        for (let i = 0; i < 5; i++) {
          const mx = hx[j] + tr(((hx[(j + 1) % heads] - hx[j]) * i) / 4);
          const my = hy[j] + tr(((hy[(j + 1) % heads] - hy[j]) * i) / 4);
          DrawCircleEx(mx - G.scroll_x, my - G.scroll_y, S(24) + Math.abs(i - 2) * S(12) + (rand() % S(6)), 0, 96 ^ flash_coeff);
        }
      }
      for (let j = 0; j < heads; j++) {
        for (let i = 0; i < 5; i++) {
          const mx = hx[j] + tr(((hx[(j + 1) % heads] - hx[j]) * i) / 4);
          const my = hy[j] + tr(((hy[(j + 1) % heads] - hy[j]) * i) / 4);
          DrawCircleEx(mx - G.scroll_x, my - G.scroll_y, S(20) + Math.abs(i - 2) * S(10) + (rand() % S(6)), 0, 0 ^ flash_coeff);
        }
      }
      for (let i = 0; i < heads; i++)
        blitGame(spr, null, screen, hx[i] - S(16) - G.scroll_x, hy[i] - S(16) - G.scroll_y, getAssetScale('dat/i/boss2.png'));
      break;
    }
    case 2: {
      const spr = getImg('dat/i/boss3.png');
      boss_m_heads = G.boss_lives;
      DrawCircleEx(boss_x - G.scroll_x, boss_y - G.scroll_y, S(48), 0, 0 ^ flash_coeff);
      for (let i = 0; i < boss_m_heads; i++) {
        let mx = boss_x;
        let my = boss_y;
        let md = ((M_PI * 2.0) / boss_m_heads) * i + M_PI / 4;
        while (dist(mx, my, boss_m_hx[i], boss_m_hy[i]) > S(12)) {
          md = CHDir(md, PDir(mx, my, boss_m_hx[i], boss_m_hy[i]), 0.5);
          mx += Math.cos(md) * S(12);
          my += Math.sin(md) * S(12);
          DrawCircleEx(mx - G.scroll_x, my - G.scroll_y, S(16) + (rand() % S(8)), 0, 0 ^ flash_coeff);
        }
      }
      for (let i = 0; i < boss_m_heads; i++)
        DrawCircleEx(boss_m_hx[i] - G.scroll_x, boss_m_hy[i] - G.scroll_y, S(28), 0, 0 ^ flash_coeff);
      for (let i = 0; i < boss_m_heads; i++) {
        let mx = boss_x;
        let my = boss_y;
        let md = ((M_PI * 2.0) / boss_m_heads) * i + M_PI / 4;
        while (dist(mx, my, boss_m_hx[i], boss_m_hy[i]) > S(12)) {
          md = CHDir(md, PDir(mx, my, boss_m_hx[i], boss_m_hy[i]), 0.5);
          mx += Math.cos(md) * S(12);
          my += Math.sin(md) * S(12);
          blitGame(
            spr,
            { x: BASE_TILE, y: BASE_TILE * (flash_coeff > 0 ? 1 : 0), w: BASE_TILE, h: BASE_TILE },
            screen,
            mx - S(16) - G.scroll_x,
            my - S(16) - G.scroll_y,
            getAssetScale('dat/i/boss3.png')
          );
        }
      }
      const df = { x: 0, y: BASE_TILE * (flash_coeff > 0 ? 1 : 0), w: BASE_TILE, h: BASE_TILE };
      for (let i = 0; i < boss_m_heads; i++)
        blitGame(spr, df, screen, boss_m_hx[i] - S(16) - G.scroll_x, boss_m_hy[i] - S(16) - G.scroll_y, getAssetScale('dat/i/boss3.png'));
      for (let i = 0; i < boss_m_heads; i++)
        blitGame(spr, df, screen, boss_x - S(16) - G.scroll_x, boss_y - S(16) - G.scroll_y, getAssetScale('dat/i/boss3.png'));
      break;
    }
    case 3: {
      const spr = getImg('dat/i/boss4.png');
      DrawCircleEx(boss_x - G.scroll_x, boss_y - G.scroll_y, S(64) + (rand() % S(4)), 0, (192 + (rand() % 64)) ^ flash_coeff);
      DrawCircleEx(boss_x - G.scroll_x, boss_y - G.scroll_y, S(48) + (rand() % S(4)), 0, (rand() % 64) ^ flash_coeff);
      blitGame(spr, null, screen, boss_x - S(16) - G.scroll_x, boss_y - S(16) - G.scroll_y, getAssetScale('dat/i/boss4.png'));
      break;
    }
  }
}

// BC_BossIntro statics
let bi_bar_fill = 0;
let bi_circle = 0;
let bi_circle_reduce = 128;

function BC_BossIntro(): void {
  if (G.boss_dlg !== 0) {
    DrawBoss();
    DrawBossHP(100);
    return;
  }
  if (bi_bar_fill < 100) {
    bi_bar_fill += 2;
    DrawBossHP(bi_bar_fill);
  } else {
    if (bi_circle < 128) {
      bi_circle += 2;
      DrawCircleEx(boss_x - G.scroll_x, boss_y - G.scroll_y, bi_circle, 0, 0);
      DrawCircle(boss_x - G.scroll_x, boss_y - G.scroll_y, bi_circle, 96);
    } else {
      if (bi_circle_reduce > 4) {
        bi_circle_reduce -= 2;
        DrawCircleEx(boss_x - G.scroll_x, boss_y - G.scroll_y, bi_circle_reduce, 0, 0);
        DrawCircle(boss_x - G.scroll_x, boss_y - G.scroll_y, bi_circle_reduce, 96);
        DrawBoss();
      } else {
        DrawBoss();
        if (final_boss_dlg === 0 && G.current_boss === 3) {
          G.boss_dlg = 1;
          final_boss_dlg = 1;
        } else {
          G.boss_fight_mode = 2;
        }
        bi_circle = 0;
        bi_bar_fill = 0;
        bi_circle_reduce = 128;
      }
    }
    DrawBossHP(100);
  }
}

// BC_BossCombat statics
let bc_t = 0;
let bc0_dir = 0.0;
let bc0_dir_offset = 1.0;
let bc2_t2 = 0;

function BC_BossCombat(): void {
  bc_t++;
  const t = bc_t;
  const pdir = PlayerDir(boss_x, boss_y);
  const pdir_x = Math.cos(pdir);
  const pdir_y = Math.sin(pdir);

  let move_x = boss_x;
  let move_y = boss_y;
  DrawBoss();

  switch (G.current_boss) {
    case 0: {
      move_x += Math.cos(bc0_dir) * (4 + tr((3 - G.boss_lives) / 2));
      move_y += Math.sin(bc0_dir) * (4 + tr((3 - G.boss_lives) / 2));
      move_x += pdir_x * (2 + tr((3 - G.boss_lives) / 2));
      move_y += pdir_y * (2 + tr((3 - G.boss_lives) / 2));

      bc0_dir += ((rand() % 64) / 256) * bc0_dir_offset;
      if (rand() % 48 === 0) bc0_dir_offset *= -1.0;
      while (!BossMovement(move_x, move_y)) {
        bc0_dir += ((rand() % 64) / 256) * bc0_dir_offset;
        if (rand() % 48 === 0) bc0_dir_offset *= -1.0;
        move_x = boss_x;
        move_y = boss_y;
        move_x += Math.cos(bc0_dir) * (4 + tr((3 - G.boss_lives) / 2));
        move_y += Math.sin(bc0_dir) * (4 + tr((3 - G.boss_lives) / 2));
        move_x += pdir_x * (2 + tr((3 - G.boss_lives) / 2));
        move_y += pdir_y * (2 + tr((3 - G.boss_lives) / 2));
      }
      if (BossMovement(move_x, move_y)) {
        boss_x = tr(move_x);
        boss_y = tr(move_y);
      }
      if (boss_flash > 15) return;

      switch (G.boss_lives) {
        case 3: {
          const laser_dmg = tr((rand() % (tr(G.player_shield / 2) + 1)) / 2);
          for (let i = 0; i < tr(G.player_shield / 5) + 1; i++) {
            SpawnBullet(boss_x + (rand() % 48) * pdir_x, boss_y + (rand() % 48) * pdir_y, 3, pdir + M_PI / 4, 2, 0);
            SpawnBullet(boss_x + (rand() % 48) * pdir_x, boss_y + (rand() % 48) * pdir_y, 0, pdir, 4.5, 0);
          }
          const l1 = pdir + 0.3 + Math.sin(t / 8) * 0.2;
          const l2 = pdir - 0.3 + Math.sin(t / 8) * 0.2;
          SpawnLaser(boss_x + pdir_x * 24, boss_y + pdir_y * 8, l1, 0, 1, 0, laser_dmg);
          SpawnLaser(boss_x + pdir_x * 24, boss_y + pdir_y * 8, l2, 0, 1, 0, laser_dmg);
          break;
        }
        case 2: {
          const laser_dmg = tr((rand() % (tr(G.player_shield / 2) + 1)) / 2);
          for (let i = 0; i < tr(G.player_shield / 3) + 1; i++) {
            SpawnBullet(boss_x + (rand() % 48) * pdir_x, boss_y + (rand() % 48) * pdir_y, 3, RandomDir(), 3.5, 0);
            SpawnBullet(boss_x + (rand() % 48) * pdir_x, boss_y + (rand() % 48) * pdir_y, 0, RandomDir(), 4.5, 0);
          }
          const l1 = pdir + 0.4 + Math.sin(t / 8) * 0.2;
          const l2 = pdir - 0.4 + Math.sin(t / 8) * 0.2;
          const l3 = RandomDir();
          SpawnLaser(boss_x + pdir_x * 24, boss_y + pdir_y * 8, l1, 0, 1, 0, laser_dmg);
          SpawnLaser(boss_x + pdir_x * 24, boss_y + pdir_y * 8, l2, 0, 1, 0, laser_dmg);
          if (t % 12 === 0) SpawnLaser(boss_x + pdir_x * 24, boss_y + pdir_y * 8, l3, 24, 8, 0, laser_dmg * 4);
          break;
        }
        case 1: {
          const laser_dmg = tr((rand() % (tr(G.player_shield / 2) + 1)) / 2);
          for (let i = 0; i < tr(G.player_shield / 4) + 1; i++) {
            SpawnBullet(boss_x + (rand() % 48) * pdir_x, boss_y + (rand() % 48) * pdir_y, 3, RandomDir(), 3.5, 0);
            SpawnBullet(boss_x + (rand() % 48) * pdir_x, boss_y + (rand() % 48) * pdir_y, 0, pdir, 5.0, 0);
            SpawnBullet(boss_x + (rand() % 48) * pdir_x, boss_y + (rand() % 48) * pdir_y, 0, RandomDir(), 4.5, 0);
          }
          const l1 = pdir + 0.4 + Math.sin(t / 8) * 0.3;
          const l2 = pdir - 0.4 + Math.sin(t / 8) * 0.3;
          SpawnLaser(boss_x + pdir_x * 24, boss_y + pdir_y * 8, l1, 0, 1, 0, laser_dmg);
          SpawnLaser(boss_x + pdir_x * 24, boss_y + pdir_y * 8, l2, 0, 1, 0, laser_dmg);
          if (t % 5 === 0) SpawnLaser(boss_x + pdir_x * 24, boss_y + pdir_y * 8, RandomDir(), 16, 16, 0, laser_dmg * 4);
          if (t % 10 === 0) SpawnBullet(boss_x + (rand() % 48) * pdir_x, boss_y + (rand() % 48) * pdir_y, 5, RandomDir(), 5, 0);
          break;
        }
      }
      break;
    }
    case 1: {
      const heads = 5 - G.boss_lives;
      const h_dist = (M_PI * 2) / heads;
      const hx: number[] = [];
      const hy: number[] = [];
      for (let i = 0; i < heads; i++) {
        hx[i] = boss_x + Math.cos(boss_2h_dir + h_dist * i) * boss_2h_dst;
        hy[i] = boss_y + Math.sin(boss_2h_dir + h_dist * i) * boss_2h_dst;
      }
      move_x += -6 + (rand() % 13);
      move_y += -6 + (rand() % 13);
      move_x += pdir_x * (2 + tr((3 - G.boss_lives) / 2));
      move_y += pdir_y * (2 + tr((3 - G.boss_lives) / 2));

      let check_pass = 1;
      for (let i = 0; i < heads; i++) {
        const mx = move_x + Math.cos(boss_2h_dir + h_dist * i) * boss_2h_dst;
        const my = move_y + Math.sin(boss_2h_dir + h_dist * i) * boss_2h_dst;
        if (!BossMovement(mx, my)) check_pass = 0;
      }
      if (check_pass) {
        boss_x = tr(move_x);
        boss_y = tr(move_y);
      }
      if (boss_flash > 30) return;

      if (t % (10 + G.boss_lives * 6) === 1) {
        const i = tr(t / (10 + G.boss_lives * 6)) % heads;
        for (let j = 0; j < tr(G.player_shield / 2) + 1; j++)
          SpawnBullet(hx[i] + (rand() % 48) * pdir_x, hy[i] + (rand() % 48) * pdir_y, 0, pdir, 7 + (rand() % 16) / 10.0, 0);
        SpawnBullet(hx[i], hy[i], 4, pdir, 10, 0);
      }
      for (let i = 0; i < tr(G.player_shield / 4) + heads; i++)
        SpawnBullet(boss_x, boss_y, 0, RandomDir(), 3 + (rand() % 16) / 10.0, 0);

      if (G.boss_lives === 2) {
        if (t % 20 === 1) SpawnBullet(boss_x, boss_y, 5, pdir, 5, 0);
      }
      if (t % (100 + G.boss_lives * 50) === 100 + G.boss_lives * 50 - 1) {
        const i = tr(G.player_shield / 3) + 2;
        const hd2 = (M_PI * 2.0) / i;
        for (let j = 0; j < i; j++)
          SpawnLaser(boss_x + Math.cos(hd2 * j) * 20, boss_y + Math.sin(hd2 * j) * 20, pdir, 15 + (rand() % 16), 5, 0, tr(G.player_shield / 3) + 1);
      }
      if (G.boss_lives === 2) {
        for (let i = 0; i < heads; i++)
          for (let j = 0; j < tr(G.player_shield / 6) + 1; j++) SpawnBullet(hx[i], hy[i], 3, RandomDir(), 6, 0);
      }
      if (G.boss_lives <= 2) {
        if (t % (12 + (G.boss_lives - 1) * 30) === 9) {
          for (let i = 0; i < heads; i++) SpawnLaser(hx[i], hy[i], pdir, 6, 6, 0, tr(G.player_shield / 6) + 1);
        }
      }
      break;
    }
    case 2: {
      boss_m_heads = G.boss_lives;
      for (let i = 0; i < boss_m_heads; i++) {
        let mx = boss_m_hx[i];
        let my = boss_m_hy[i];
        boss_m_hd[i] = CHDir(boss_m_hd[i], PlayerDir(mx, my), 0.2);
        let md = ((M_PI * 2.0) / boss_m_heads) * i + M_PI / 4;
        boss_m_hd[i] = CHDir(boss_m_hd[i], md, 0.1);
        md = boss_m_hd[i];

        if (t % boss_m_heads === i) {
          mx += Math.cos(md) * 4 + (boss_m_heads === 1 ? 1 : 0) * 2;
          my += Math.sin(md) * 4 + (boss_m_heads === 1 ? 1 : 0) * 2;
        }
        for (let j = 0; j < boss_m_heads; j++) {
          if (j !== i) {
            if (dist(boss_m_hx[j], boss_m_hy[j], boss_m_hx[i], boss_m_hy[i]) < 200) {
              md = PDir(boss_m_hx[j], boss_m_hy[j], boss_m_hx[i], boss_m_hy[i]);
              mx += Math.cos(md) * 2;
              my += Math.sin(md) * 2;
              boss_m_hd[i] = CHDir(boss_m_hd[i], md, 0.1);
            }
          }
        }
        let trying = 10;
        while (!BossMovement(mx, my)) {
          trying--;
          if (trying === 0) break;
          md = PlayerDir(boss_m_hx[i], boss_m_hy[i]);
          md = md - 0.3 + (rand() % 16) * 0.6;
          mx = boss_m_hx[i] + Math.cos(md) * 5;
          my = boss_m_hy[i] + Math.sin(md) * 5;
          if (boss_m_heads === 1) {
            boss_m_hd[i] = CHDir(boss_m_hd[i], PlayerDir(mx, my), 0.2);
            md = boss_m_hd[i];
            mx += Math.cos(md) * 3;
            my += Math.sin(md) * 3;
          }
        }
        if (trying > 0) {
          boss_m_hx[i] = tr(mx);
          boss_m_hy[i] = tr(my);
        }
      }
      if (boss_flash > 30) return;

      if (boss_m_heads > 1) {
        if (t % 2 === 0) {
          const i = t % boss_m_heads;
          SpawnBullet(boss_m_hx[i] + Math.cos(boss_m_hd[i]) * 8, boss_m_hy[i] + Math.sin(boss_m_hd[i]) * 8, 0, boss_m_hd[i], 10, 0);
        } else {
          const i = (t + 2) % boss_m_heads;
          const md = PlayerDir(boss_m_hx[i], boss_m_hy[i]);
          SpawnBullet(boss_m_hx[i] + Math.cos(boss_m_hd[i]) * 8, boss_m_hy[i] + Math.sin(boss_m_hd[i]) * 8, 0, md, 10, 0);
        }
      } else {
        const md = CHDir(RandomDir(), PlayerDir(boss_m_hx[0], boss_m_hy[0]), 1.5);
        SpawnBullet(boss_m_hx[0] + Math.cos(boss_m_hd[0]) * 8, boss_m_hy[0] + Math.sin(boss_m_hd[0]) * 8, 0, md, 10, 0);
      }
      for (let i = 0; i < 24; i++) {
        if (bc2_t2 % (6 - tr(G.player_shield / 6) + boss_m_heads) === 0) {
          let md = RandomDir();
          if (boss_m_heads > 2) {
            SpawnBullet(boss_x + Math.cos(md) * 8, boss_y + Math.sin(md) * 8, 0, md, 5, 0);
          } else if (boss_m_heads > 1) {
            SpawnBullet(boss_x + Math.cos(md) * 24, boss_y + Math.sin(md) * 24, 4, md, 12, 0);
          } else {
            md = CHDir(md, pdir, 0.66);
            SpawnBullet(boss_x + Math.cos(md) * 24, boss_y + Math.sin(md) * 24, 4, md, 12, 0);
          }
        }
        bc2_t2++;
      }
      if (boss_m_heads < 4) {
        if (t % 5 === 0) {
          const i = tr(t / 5) % boss_m_heads;
          let md = boss_m_hd[i];
          SpawnLaser(boss_m_hx[i], boss_m_hy[i], md, 1, 10, 0.05, tr(G.player_shield / 7) + 3 - boss_m_heads);
          md += M_PI / 2;
          SpawnLaser(boss_m_hx[i], boss_m_hy[i], md, 1, 10, 0.05, tr(G.player_shield / 7) + 3 - boss_m_heads);
          md += M_PI / 2;
          SpawnLaser(boss_m_hx[i], boss_m_hy[i], md, 1, 10, 0.05, tr(G.player_shield / 7) + 3 - boss_m_heads);
          md += M_PI / 2;
          SpawnLaser(boss_m_hx[i], boss_m_hy[i], md, 1, 10, 0.05, tr(G.player_shield / 7) + 3 - boss_m_heads);
        }
      }
      if (boss_m_heads === 1) {
        if (t % 30 < G.player_shield) {
          const md = CHDir(RandomDir(), pdir, 1.0);
          SpawnBullet(boss_m_hx[0] + Math.cos(md) * 24, boss_m_hy[0] + Math.sin(md) * 24, 3, md, 11, 0);
        }
      }
      break;
    }
    case 3: {
      let mx = boss_x + Math.cos(boss_dir) * (4 + (G.player_shield === 30 ? 1 : 0));
      let my = boss_y + Math.sin(boss_dir) * (4 + (G.player_shield === 30 ? 1 : 0));
      if (BossMovement(mx, my)) {
        boss_x = tr(mx);
        boss_y = tr(my);
        boss_dir = CHDir(boss_dir, pdir, 0.3);
      } else {
        boss_dir = CHDir(boss_dir, RandomDir(), 1);
      }

      if (G.boss_lives === 1 && G.player_shield === 30) {
        const boss_x_bias = 1.33 - boss_hp / 1200.0;
        const boss_y_bias = 0.7 + boss_hp / 1500.0;
        const room_x = rooms[G.player_room].x * TILE + S(64);
        const room_y = rooms[G.player_room].y * TILE + S(64);
        const room_w = rooms[G.player_room].w * TILE - S(128);
        const room_h = rooms[G.player_room].h * TILE - S(128);
        const tmr_t = t / 30.0;
        mx = tr((Math.sin(tmr_t * boss_x_bias) * 0.5 + 0.5) * room_w) + room_x;
        my = tr((Math.cos(tmr_t * boss_y_bias) * 0.5 + 0.5) * room_h) + room_y;
        if (dist(mx, my, boss_x, boss_y) < S(24)) {
          boss_x = tr(mx);
          boss_y = tr(my);
        } else {
          const b_m_dir = PDir(boss_x, boss_y, mx, my);
          boss_x += tr(Math.cos(b_m_dir) * S(24));
          boss_y += tr(Math.sin(b_m_dir) * S(24));
        }
        if (t % 50 < 49) {
          G.proxy_seek = 0;
          for (let i = 0; i < 20; i++) {
            const firedir = RandomDir();
            SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 7, firedir, 1, 0);
          }
        } else {
          G.proxy_seek = 1;
        }
        if (tr(t / 50) % 4 === 3) G.proxy_seek = 1;
        for (let i = 0; i < 24; i++) {
          const firedir = (M_PI / 15) * i + (t / 33.0) * M_PI;
          SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 0, firedir, 10, 0);
        }
        break;
      }

      const boss_loop_total = 1 + (G.player_shield === 30 ? 1 : 0) * 2;
      let cboss_dir = boss_dir;
      for (let boss_loop = 0; boss_loop < boss_loop_total; boss_loop++) {
        let npattern: number;
        if (G.player_shield === 30) npattern = (3 - G.boss_lives) * 3 + 2 - tr(boss_hp / 334);
        else npattern = (2 - G.boss_lives) * 3 + 2 - tr(boss_hp / 334);

        switch (npattern) {
          case 0:
            for (let i = 0; i < 4; i++) {
              const firedir = (M_PI / 2) * i + (t / 33.0) * M_PI;
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 0, firedir, 5, 0);
            }
            for (let i = 0; i < 4; i++) {
              const firedir = (M_PI / 2) * i + (t / 33.0) * M_PI * 2.0;
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 0, firedir, 8, 0);
            }
            for (let i = 0; i < 4; i++) {
              const firedir = RandomDir();
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 0, firedir, 12, 0);
            }
            break;
          case 1:
            if (t % 100 < 80) {
              for (let i = 0; i < 6; i++) {
                const firedir = RandomDir();
                SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 7, firedir, 1, 0);
              }
            } else {
              if (t % 100 === 88) G.proxy_seek = 1;
              if (t % 100 === 92) G.proxy_seek = 0;
            }
            for (let i = 0; i < 8; i++) {
              const firedir = (M_PI / 4) * i + (t / 33.0) * M_PI;
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 0, firedir, 10, 0);
            }
            break;
          case 2:
            if (t % 4 === 2) {
              const firedir = cboss_dir + (t / 33.0) * M_PI;
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 8, firedir, 10, 1);
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 8, firedir, 5, 1);
            }
            for (let i = 0; i < 4; i++) {
              const firedir = cboss_dir + (M_PI / 6) * i + (t / 33.0) * M_PI;
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 0, firedir, 5, 0);
            }
            break;
          case 3:
            if (t % 30 === 29) {
              const firedir = RandomDir();
              for (let s = 0; s < 7; s++)
                SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 8, firedir, 10 - s * 0.01, 1);
            }
            if (t % 40 === 39) {
              const firedir = RandomDir();
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 1, firedir, 6, 1);
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 5, firedir + 1, 6, 1);
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 6, firedir + 1, 6, 1);
            }
            if (t % 50 === 49) {
              G.proxy_seek = 0;
              let firedir = RandomDir();
              for (let i = 0; i < 60; i++) {
                SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 7, firedir, 1, 1);
                firedir += (M_PI * 2.0 * firedir) / 60.0;
              }
            }
            if (t % 50 === 45) G.proxy_seek = 1;
            if (t % 10 === 2) {
              const firedir = cboss_dir + (t / 33.0) * M_PI;
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 8, firedir, 10, 1);
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 8, firedir, 7.5, 1);
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 8, firedir, 5, 1);
            }
            break;
          case 4: {
            if (t % 80 === 79) {
              const firedir = RandomDir();
              for (let s = 0; s < 5; s++)
                SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 8, firedir, 10 - s * 0.02, 1);
            }
            if (t % 90 === 89) {
              const firedir = RandomDir();
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 1, firedir, 6, 1);
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 5, firedir + 1, 6, 1);
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 6, firedir + 1, 6, 1);
            }
            if (t % 100 === 99) {
              G.proxy_seek = 0;
              let firedir = RandomDir();
              for (let i = 0; i < 60; i++) {
                SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 7, firedir, 1, 1);
                SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 7, firedir, 2, 1);
                SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 7, firedir, 3, 1);
                SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 7, firedir, 4, 1);
                firedir += (M_PI * 2.0 * firedir) / 60.0;
              }
            }
            if (t % 100 === 95) G.proxy_seek = 1;
            const flp = (t % 50) / 49.0;
            if (t % 10 === 3) {
              const i = tr(t / 10) % 4;
              let ls_x: number;
              let ls_y: number;
              switch (i) {
                case 0: ls_x = S(7904) + flp * S(576); ls_y = S(8000); break;
                case 1: ls_x = S(8480) - flp * S(576); ls_y = S(8416); break;
                case 2: ls_x = S(7904); ls_y = S(8000) + flp * S(416); break;
                default: ls_x = S(8480); ls_y = S(8416) - flp * S(416); break;
              }
              const firedir = PlayerDir(ls_x, ls_y) - 0.1 + (rand() % 16) / 8.0;
              SpawnLaser(ls_x, ls_y, firedir, 8, 2, 0, tr(G.player_shield / 2) + 1);
            }
            if (t % 6 === 2) {
              const firedir = cboss_dir + (t / 33.0) * M_PI;
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 8, firedir, 10, 1);
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 8, firedir, 5, 1);
            }
            break;
          }
          case 5: {
            const flp = (t % 50) / 49.0;
            if (t % 8 === 7) {
              const i = tr(t / 8) % 4;
              let ls_x: number;
              let ls_y: number;
              switch (i) {
                case 0: ls_x = S(7904) + flp * S(576); ls_y = S(8000); break;
                case 1: ls_x = S(8480) - flp * S(576); ls_y = S(8416); break;
                case 2: ls_x = S(7904); ls_y = S(8000) + flp * S(416); break;
                default: ls_x = S(8480); ls_y = S(8416) - flp * S(416); break;
              }
              const firedir = PlayerDir(ls_x, ls_y) - 0.1 + (rand() % 16) / 8.0;
              SpawnLaser(ls_x, ls_y, firedir - 0.03 * 6, 5, 5, 0.03, tr(G.player_shield / 3) + 1);
            }
            for (let i = 0; i < 7; i++) {
              const firedir = cboss_dir + (M_PI / 2) * i + (t / 33.0) * M_PI;
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 0, firedir, 8, 0);
            }
            for (let i = 0; i < 7; i++) {
              const firedir = cboss_dir + M_PI / 4 + (M_PI / 2) * i + (t / 33.0) * M_PI;
              SpawnBullet(boss_x + Math.cos(firedir) * 12, boss_y + Math.sin(firedir) * 12, 3, firedir, 8, 0);
            }
            break;
          }
        }
        cboss_dir += -0.1 + (rand() % 16) / 8.0;
      }
      break;
    }
  }
}

// BC_BossDying statics
let bd_t_timer = 0;
let bd_bxp = 0;
let bd_bdef = 0;
let bd_dr = 0;

function BC_BossDying(): void {
  if (G.current_boss < 3) {
    G.specialmessage = 40 + rooms[G.player_room].room_param;
    G.specialmessagetimer = 120;
    rooms[G.player_room].room_type = 4;
    G.boss_fight_mode = 0;
    G.current_boss += 1;
    G.artifacts[8 + rooms[G.player_room].room_param] = 0;

    CullEnemies(4);

    for (let y = 0; y < rooms[G.player_room].h; y++) {
      for (let x = 0; x < rooms[G.player_room].w; x++) {
        const rx = x + rooms[G.player_room].x;
        const ry = y + rooms[G.player_room].y;
        const rt = Get(rx, ry);
        if (rt >= 21 && rt <= 24) {
          Put(rx, ry, rt - 21 + 13, G.player_room);
          if (rt === 21) Put(rx, ry + 1, 14, GetRoom(rx, ry + 1));
          if (rt === 22) Put(rx, ry - 1, 13, GetRoom(rx, ry - 1));
          if (rt === 23) Put(rx + 1, ry, 16, GetRoom(rx + 1, ry));
          if (rt === 24) Put(rx - 1, ry, 15, GetRoom(rx - 1, ry));
        }
      }
    }
    if (G.current_boss > 0) SoupUpEnemies();
  } else {
    if (G.boss_fight_mode === 3) {
      bd_t_timer = 0;
      G.boss_fight_mode = 4;
      G.magic_circuit = 0;
      bd_bxp = 4;
      bd_bdef = 256;
      bd_dr = RandomDir();
      if (G.training) bd_t_timer = 2000;
    } else {
      DrawArtifactOverhead(3);
      if (bd_bdef > 0) {
        DrawCircleEx(boss_x - G.scroll_x, boss_y - G.scroll_y, bd_bxp, bd_bxp - bd_bdef, 0);
        bd_bxp += 4;
        bd_bdef -= 2;
      } else {
        if (bd_t_timer < 220) {
          draw_text(S(244), S(100), '*** Divine Seal ***', 1);
          draw_text(S(244), S(380), '*** Divine Seal ***', 1);
          G.magic_circuit = tr((G.circuit_size * 0.75 * bd_t_timer) / 220);
          for (let i = 0; i < 10; i++) {
            const rt = (rand() % 350) + 50;
            DrawCircle(
              G.player_x + PLAYERW / 2 - G.scroll_x + Math.cos(bd_dr) * rt,
              G.player_y - G.scroll_y + PLAYERH / 2 + Math.sin(bd_dr) * rt,
              (rand() % 30) + 5,
              (rand() % 128) + 128
            );
          }
          bd_dr -= 0.025;
          for (let i = 0; i < 5; i++) {
            Arc(G.player_x + PLAYERW / 2 - G.scroll_x, G.player_y + PLAYERH / 2 - G.scroll_y, 450, bd_dr);
            bd_dr += 0.01;
          }
          bd_dr += 0.03;
          if (bd_t_timer % 30 === 29) bd_dr = RandomDir();
          DrawCircle(G.player_x - G.scroll_x + PLAYERW / 2, G.player_y - G.scroll_y + PLAYERH / 2, (bd_t_timer % 30) * 15, 255);
          DrawCircle(G.player_x - G.scroll_x + PLAYERW / 2, G.player_y - G.scroll_y + PLAYERH / 2, ((bd_t_timer + 15) % 30) * 15, 255);
        } else {
          G.magic_circuit = 0;
          if (G.boss_fight_mode < 23) {
            if (bd_t_timer % 4 === 3) G.boss_fight_mode++;
          } else {
            if (G.training) {
              blitGame(getImg('dat/i/wuss_ending.png'), null, screen, 0, 0, getAssetScale('dat/i/wuss_ending.png'));
            } else {
              G.show_ending = 1;
            }
          }
        }
        if (bd_t_timer === 200) CullEnemies(1);
        bd_t_timer++;
      }
    }
  }
}

// BC_NewLife statics
let nl_circle_size = 0;
let nl_circle_size2 = 128;

function BC_NewLife(): void {
  if (boss_flash > 0) {
    DrawBoss();
    return;
  }
  if (boss_new_life === 1) {
    nl_circle_size = 0;
    nl_circle_size2 = 128;
    boss_ox = rooms[G.current_boss_room].w * S(16) + rooms[G.current_boss_room].x * TILE;
    boss_oy = rooms[G.current_boss_room].h * S(16) + rooms[G.current_boss_room].y * TILE;
    boss_new_life = 2;
  } else {
    if (nl_circle_size < 128) {
      nl_circle_size += 4;
      DrawCircleEx(boss_x - G.scroll_x, boss_y - G.scroll_y, nl_circle_size, 0, 0);
      DrawCircle(boss_x - G.scroll_x, boss_y - G.scroll_y, nl_circle_size, 96);
      DrawBoss();
      if (G.boss_lives > 1) {
        DrawCircleEx(boss_ox - G.scroll_x, boss_oy - G.scroll_y, nl_circle_size, 0, 0);
        DrawCircle(boss_ox - G.scroll_x, boss_oy - G.scroll_y, nl_circle_size, 96);
      }
    } else {
      if (nl_circle_size2 === 128) {
        boss_ox = boss_x;
        boss_oy = boss_y;
        boss_x = rooms[G.current_boss_room].w * S(16) + rooms[G.current_boss_room].x * TILE;
        boss_y = rooms[G.current_boss_room].h * S(16) + rooms[G.current_boss_room].y * TILE;
        boss_tail_len = 0;
        G.boss_lives--;
      }
      if (nl_circle_size2 > 4) {
        nl_circle_size2 -= 4;
        if (G.boss_lives > 0) {
          DrawCircleEx(boss_x - G.scroll_x, boss_y - G.scroll_y, nl_circle_size2, 0, 0);
          DrawCircle(boss_x - G.scroll_x, boss_y - G.scroll_y, nl_circle_size2, 96);
          DrawBoss();
        }
        DrawCircleEx(boss_ox - G.scroll_x, boss_oy - G.scroll_y, nl_circle_size2, 0, 0);
        DrawCircle(boss_ox - G.scroll_x, boss_oy - G.scroll_y, nl_circle_size2, 96);
      } else {
        if (G.boss_lives > 0) {
          boss_new_life = 0;
          boss_hp = 1000;
          if (G.boss_lives === 1 && G.current_boss === 3 && G.player_shield === 30) {
            G.player_hp = 6;
            boss_dmgrate = 0.2;
          }
        } else {
          boss_new_life = 0;
          G.boss_fight_mode = 3;
        }
      }
    }
  }
}

export function BossControl(): void {
  if (G.player_room !== G.current_boss_room) {
    G.resetboss = 0;
    G.current_boss_room = 0;
    boss_tail_len = 0;
    G.boss_fight_mode = 0;
    boss_new_life = 0;
  }
  if (G.boss_fight_mode === 1) {
    BC_BossIntro();
    return;
  }
  if (G.boss_fight_mode === 2) {
    if (boss_new_life > 0) BC_NewLife();
    else BC_BossCombat();
    return;
  }
  if (G.boss_fight_mode >= 3) {
    BC_BossDying();
    return;
  }
}

let dao_tick = 0;
export function DrawArtifactOverhead(p_obj: number): void {
  const p_x = G.player_x - S(8);
  const p_y = G.player_y - S(36) + Math.sin(dao_tick / 20.0) * S(4);
  blitGame(
    artifactSpr(),
    { x: (8 + p_obj) * BASE_TILE, y: 0, w: BASE_TILE, h: BASE_TILE },
    screen,
    p_x - G.scroll_x,
    p_y - G.scroll_y,
    getAssetScale('dat/i/artifacts.png')
  );
  dao_tick++;
}

export function CanGetArtifact(): number {
  const n_artifacts = G.current_boss + G.artifacts[8] + G.artifacts[9] + G.artifacts[10];
  const required_enemies = tr((G.total_enemies * percent_required[n_artifacts]) / 100);
  if (G.killed_enemies >= required_enemies) return 1;
  return 0;
}

export function BossRoom(room: number): void {
  G.boss_fight_mode = 1;
  G.current_boss_room = room;
  boss_x = rooms[room].w * S(16) + rooms[room].x * TILE;
  boss_y = rooms[room].h * S(16) + rooms[room].y * TILE;
  boss_hp = 1000;
  boss_flash = 0;
  G.magic_circuit = 0;
  G.boss_dlg = 0;
  boss_new_life = 0;

  switch (G.current_boss) {
    case 0:
      G.boss_lives = 3;
      boss_breakpoint = 75;
      boss_dmgrate = 1.0;
      break;
    case 1:
      G.boss_lives = 3;
      boss_breakpoint = 75;
      boss_dmgrate = 0.8;
      break;
    case 2:
      G.boss_lives = 4;
      boss_breakpoint = 90;
      boss_dmgrate = 1.25;
      boss_m_heads = 4;
      for (let i = 0; i < boss_m_heads; i++) {
        boss_m_hd[i] = (M_PI / 2) * i + M_PI / 4;
        boss_m_hx[i] = boss_x + Math.cos(boss_m_hd[i]) * S(128);
        boss_m_hy[i] = boss_y + Math.sin(boss_m_hd[i]) * S(128);
      }
      break;
    case 3:
      G.boss_lives = 2 + (G.player_shield === 30 ? 1 : 0);
      boss_breakpoint = 120;
      boss_dmgrate = 0.4 + 0.1 * (G.player_shield === 30 ? 1 : 0);
      boss_dir = (M_PI * 3.0) / 2.0;
      break;
  }

  if (G.training) {
    boss_dmgrate *= 1.2;
    boss_breakpoint *= 0.8;
  }
}
